// Hero slide responsive visibility.
//
// Each hero slide can be set (per block, `visible_on`) to show on Desktop & mobile / Desktop only /
// Mobile only. Liquid is server-side and can't know the viewport, so we apply it here: at the 749/750px
// boundary we hide the off-viewport slides with the native `[hidden]` attribute (the slideshow's own
// `slides` getter already filters `[hidden]` out of count / arrows / swipe / autoplay), and we keep the
// server-rendered pagination dots in sync.
//
// The dots are the fiddly part. They're rendered one-per-block with a hardcoded `on:click="/select/{i}"`
// (an index into the slideshow's FILTERED slide list) and the active dot is highlighted by the engine
// via `aria-selected` mapped over its captured `refs.dots` (by position). So when we hide slides we:
//   • detach the dots for hidden slides and renumber the surviving dots' on:click to their new filtered
//     index, then
//   • "reboot" the slideshow (remove + re-insert) so its connectedCallback re-runs setup and RE-CAPTURES
//     refs against the filtered DOM (count + active-dot mapping line back up).
// Before the reboot we point `initial-slide` at the currently-active slide's position within the kept
// set, so the reboot restores the visitor's place instead of snapping to the first slide. After it we
// re-run the height equalizer against the now-filtered set. The per-dot scroll-timeline
// (`animation-timeline:--slide-{i}`) is left alone — it tracks its own slide, still correct.
//
// In the theme editor we DON'T hide anything (Shopify.designMode) so every slide stays reachable to edit.

(() => {
  const SELECTOR = 'slideshow-component.hero-slider';
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const inEditor = () => !!(window.Shopify && window.Shopify.designMode);

  const visibleForViewport = (visibleOn, isMobile) =>
    visibleOn === 'all' ||
    (isMobile && visibleOn === 'mobile') ||
    (!isMobile && visibleOn === 'desktop');

  // Re-run the slideshow's own connectedCallback setup against the current (filtered) DOM, optionally
  // restoring to a chosen slide index. disconnectedCallback tears down its observers/listeners;
  // connectedCallback re-reads the filtered count, re-captures refs (the pruned dot set), and restores.
  //
  // Why detach + re-insert (rather than an in-place re-init): the base Component exposes updatedCallback()
  // which re-captures refs WITHOUT reconnecting — but that ALONE is not enough here. Slideshow's
  // slide-dependent setup (the Scroller, the IntersectionObserver that derives the visible set, autoplay,
  // and the scroll-to-initial-slide) all live in connectedCallback/#setupSlideshow, and the matching
  // teardown that prevents observer/listener leaks lives in disconnectedCallback. Slideshow does NOT
  // override updatedCallback to re-run that setup, and re-invoking connectedCallback() in place WITHOUT a
  // disconnectedCallback would double-bind listeners and leak the old Scroller + IntersectionObserver
  // (which would keep observing the now-hidden slides). There is no safe in-place re-init exposed, so the
  // reconnection is genuinely required to re-derive count/scroller geometry/visible-set against the
  // filtered DOM. The remove/insert is synchronous (no paint between the two statements), and the section
  // wrapper carries an inline min-height reservation (--hero-reserve-* in hero-slider.liquid) so the box
  // height is held even if a sub-frame of the reconnection paints — no CLS on the above-fold LCP element.
  const reboot = (el, initialSlide) => {
    const parent = el.parentNode;
    if (!parent) return;
    const sibling = el.nextSibling;
    el.remove();
    if (initialSlide != null) el.setAttribute('initial-slide', String(initialSlide));
    parent.insertBefore(el, sibling);
  };

  class HeroVisibility {
    constructor(component) {
      this.component = component;
      this.raf = 0;
      // Cache the full, ordered slide + dot lists once — the source of truth for restoring on resize.
      this.slides = Array.from(component.querySelectorAll('slideshow-slide'));
      this.dotList = component.querySelector('.slideshow-controls__dots');
      this.dots = this.dotList ? Array.from(this.dotList.querySelectorAll('li')) : [];
      this.counterTotal = component.querySelector('.slideshow-controls__counter');

      this.apply = this.apply.bind(this);
      this.onChange = this.onChange.bind(this);

      this.apply();
      if (MOBILE.addEventListener) MOBILE.addEventListener('change', this.onChange);
      else if (MOBILE.addListener) MOBILE.addListener(this.onChange); // older Safari
    }

    // Debounce rapid breakpoint crossings to one frame so we never reboot twice in a tick.
    onChange() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.apply();
      });
    }

    apply() {
      const editor = inEditor();
      const isMobile = !editor && MOBILE.matches;

      // keep[i] = should slide i be shown for this viewport. The editor shows every slide so the
      // merchant can select/edit any of them.
      const keep = this.slides.map((slide) => {
        if (editor) return true;
        const frame = slide.querySelector('.hero-slide__frame');
        const v = (frame && frame.dataset.visibleOn) || 'all';
        return visibleForViewport(v, isMobile);
      });

      // Never hide every slide on a viewport — keep the first one.
      if (keep.length && !keep.some(Boolean)) keep[0] = true;

      // Reboot only when a slide's desired hidden-state differs from its current state.
      const needsReboot = this.slides.some((slide, i) => slide.hasAttribute('hidden') === keep[i]);
      if (!needsReboot) {
        this.component.classList.add('hero-vis-ready');
        return;
      }

      // Preserve the visitor's place: the engine resets to `initial-slide` on reconnect, so point it at
      // the active slide's position WITHIN the kept set (its new filtered index). If the active slide is
      // the one being hidden, fall back to the first visible slide (0).
      let initial = 0;
      const activeIndex = this.slides.findIndex((s) => s.getAttribute('aria-hidden') === 'false');
      if (activeIndex >= 0 && keep[activeIndex]) {
        initial = keep.slice(0, activeIndex).filter(Boolean).length;
      }

      // 1) Slides: hide the dropped ones, show the kept ones, and renumber each kept slide's own
      //    `on:click="/select/{i}"` to its new FILTERED index — a stale index resolves to a different
      //    slide, so the engine preventDefaults the click and the slide's CTA links stop working.
      let keptSlideIndex = 0;
      this.slides.forEach((slide, i) => {
        slide.toggleAttribute('hidden', !keep[i]);
        if (!keep[i]) return;
        if (slide.hasAttribute('on:click')) slide.setAttribute('on:click', `/select/${keptSlideIndex}`);
        keptSlideIndex += 1;
      });

      // 2) Dots: detach dropped ones, restore kept ones at their original ordinal, and renumber the
      //    surviving dots' on:click to their new FILTERED index (position among kept).
      if (this.dots.length) {
        let filteredIndex = 0;
        this.dots.forEach((li, i) => {
          const button = li.querySelector('.slideshow-control');
          if (keep[i]) {
            if (!li.isConnected) {
              const after = this.dots.slice(i + 1).find((d) => d.isConnected);
              if (after) this.dotList.insertBefore(li, after);
              else this.dotList.appendChild(li);
            }
            if (button) button.setAttribute('on:click', `/select/${filteredIndex}`);
            filteredIndex += 1;
          } else if (li.isConnected) {
            li.remove();
          }
        });
      }

      // 3) Counter pagination: fix the "/N" denominator to the visible count. The markup is
      //    `<span ref="current">1</span><span class="slash">/</span>N`, so the last child is the bare
      //    total text node.
      if (this.counterTotal) {
        const visibleCount = keep.filter(Boolean).length;
        const node = this.counterTotal.lastChild;
        if (node && node.nodeType === Node.TEXT_NODE) node.textContent = String(visibleCount);
      }

      // 4) Re-derive count / refs / active dot / scroll against the filtered DOM, restoring position.
      this.component.classList.add('hero-vis-ready');
      reboot(this.component, initial);

      // 5) Re-measure equal heights against the now-visible set (a hidden slide must not contribute).
      if (this.component.__heroEqualizer && typeof this.component.__heroEqualizer.run === 'function') {
        this.component.__heroEqualizer.run();
      }
    }

    destroy() {
      if (this.raf) cancelAnimationFrame(this.raf);
      if (MOBILE.removeEventListener) MOBILE.removeEventListener('change', this.onChange);
      else if (MOBILE.removeListener) MOBILE.removeListener(this.onChange);
    }
  }

  const instances = new Set();

  const init = (component) => {
    if (component.__heroVisibility) return;
    component.__heroVisibility = new HeroVisibility(component);
    instances.add(component.__heroVisibility);
  };

  const reap = () => {
    instances.forEach((inst) => {
      if (!document.contains(inst.component)) {
        inst.destroy();
        inst.component.__heroVisibility = null;
        instances.delete(inst);
      }
    });
  };

  const boot = () => document.querySelectorAll(SELECTOR).forEach(init);

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  // Theme editor: a section re-render swaps in a fresh slideshow (and the bound flag is on the new node).
  document.addEventListener('shopify:section:load', (event) => {
    reap();
    const root = event.target;
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (root.matches && root.matches(SELECTOR)) init(root);
    root.querySelectorAll(SELECTOR).forEach(init);
  });
  document.addEventListener('shopify:section:unload', reap);
})();
